import type { SyntheticEvent } from "react";

// A checkbox or a button input is a control like any other. Keep in sync
// with the `.title` rule in Board/BoardCard.module.scss.
const NON_TEXT_INPUT_TYPES = [
    "button",
    "checkbox",
    "color",
    "file",
    "hidden",
    "image",
    "radio",
    "range",
    "reset",
    "submit",
];

const TEXT_FIELD_SELECTOR = [
    "input" +
        NON_TEXT_INPUT_TYPES.map((type) => `:not([type='${type}'])`).join(""),
    "textarea",
    "[contenteditable='true']",
].join(", ");

/** True when `target` is, or sits inside, a text field. */
export const isInTextField = (target: EventTarget | null) =>
    target instanceof Element && target.closest(TEXT_FIELD_SELECTOR) !== null;

/**
 * True when the event started in a text field, such as an inline rename
 * inside a card or a right-click menu's trigger: it keeps focus, its own
 * clicks and the browser's context menu while it's typed in.
 */
export const isFromTextField = ({ target }: Pick<SyntheticEvent, "target">) =>
    isInTextField(target);

/**
 * True while a text field inside `container` has focus. A click then
 * belongs to the field even when it lands outside it: a text selection
 * dragged out of the field ends with a click on the field's ancestor.
 */
export const hasTextFieldFocus = (container: Element) => {
    const { activeElement } = container.ownerDocument;
    return (
        activeElement !== null &&
        container.contains(activeElement) &&
        activeElement.matches(TEXT_FIELD_SELECTOR)
    );
};
